/*
 * Технические работы: что показывать и когда перестать.
 *
 * Состояние приходит тремя путями — спрашиваем при запуске, слышим по сокету
 * и получаем в отказе любого запроса. Пути разные, потому что во время работ
 * сокет закрыт, а обычные запросы отвергаются: остаётся опрос.
 */

import type { MaintenanceState } from '@andresot/protocol/envelope';
import { fetchMaintenance } from '$api/maintenance';
import { onMaintenance } from '$api/client';

/** Как часто спрашивать сервер, пока экран показан. */
const POLL_MS = 30_000;

class MaintenanceStore {
	state = $state<MaintenanceState | null>(null);
	/** Персоналу экран не показываем: администрация работает во время работ. */
	staff = $state(false);

	private timer: ReturnType<typeof setInterval> | null = null;

	get active(): boolean {
		return this.state?.active === true;
	}

	/** Показывать ли экран прямо сейчас. */
	get blocking(): boolean {
		return this.active && !this.staff;
	}

	/**
	 * Подписаться на отказы сервера. Ставится один раз при запуске
	 * приложения — до первого запроса, чтобы не пропустить самый ранний отказ.
	 */
	listen(): void {
		onMaintenance((state) => this.apply(state));
	}

	apply(state: MaintenanceState): void {
		const wasBlocking = this.blocking;
		this.state = state;

		if (state.active) {
			this.startPolling();
			return;
		}

		this.stopPolling();

		/*
		 * Работы кончились — перезагружаем страницу.
		 *
		 * Пока они шли, мессенджер не запускался и соединение было закрыто:
		 * поднимать всё это по кусочкам значило бы повторять запуск приложения
		 * второй раз, в другом порядке и с другими ошибками. Перезагрузка
		 * возвращает человека в рабочее приложение сама.
		 */
		if (wasBlocking && typeof window !== 'undefined') window.location.reload();
	}

	/** Узнать состояние у сервера. Ошибку глотаем: нет ответа — нет экрана. */
	async refresh(): Promise<void> {
		try {
			this.apply(await fetchMaintenance());
		} catch {
			/* Сеть могла отвалиться — этим занимается карточка обрыва связи. */
		}
	}

	/*
	 * Отсчёт до нуля сам ничего не открывает: закончились работы или нет,
	 * решает сервер, а не часы на устройстве. Поэтому по нулю мы просто
	 * спрашиваем ещё раз.
	 */
	private startPolling(): void {
		if (this.timer) return;
		this.timer = setInterval(() => void this.refresh(), POLL_MS);
	}

	private stopPolling(): void {
		if (this.timer) clearInterval(this.timer);
		this.timer = null;
	}

	/** Отсчёт кончился — проверяем у сервера, не пора ли открывать. */
	countdownFinished(): void {
		void this.refresh();
	}
}

export const maintenance = new MaintenanceStore();
